import type { ReactNode } from 'react'
import { motion } from 'framer-motion'
import { Avatar } from '@/components/Avatar'
import { XpBar, LevelRow } from '@/components/XpBar'
import { PaintedIcon, type PaintedIconId } from '@/components/PaintedIcon'
import { cardBgInk, cardBgStyle } from '@/data/playerCards'
import { CardBg } from '@/components/CardBg'
import { FounderTag } from '@/components/FounderTag'
import { FounderPill } from '@/features/profile/FounderSupport'
import { denominationColor, denominationName } from '@/data/denominations'
import { aboutVerseSeed, hasAbout, translationByCode } from '@/data/cardAbout'
import type { AvatarSpec } from '@/types'

// Everything a player card renders. Deliberately a plain data bag rather than a
// Profile, because the same card is drawn for other players from the
// get_player_card RPC, which returns only public fields.
export interface PlayerCardData {
  username: string
  avatarEmoji: string
  avatarCharacter?: AvatarSpec | null
  avatarBorder?: string
  avatarBadge?: string | null
  cardBackground?: string | null
  /** Equipped pet id (data/pets.ts). Drawn by ProfileHero above the card, never
   *  by the card itself — see the note on statsOnly. */
  pet?: string | null
  /** What the player says about themselves (data/cardAbout) — a verse
   *  reference, one of the 66 books, a translation code. Drawn as one slim
   *  strip above the stats, and only when any of the three is set. */
  favoriteVerse?: string | null
  favoriteBook?: string | null
  favoriteTranslation?: string | null
  xp: number
  level: number
  currentStreak: number
  longestStreak: number
  totalPlays: number
  cards: number
  denomination?: string | null
  /** Earned road title, shown under the name. Only ever set for your own card:
   *  the leaderboard RPCs don't return other players' titles yet, and a title
   *  that renders for you and vanishes for everyone else would read as a bug.
   *  Widening it means adding the column to those RPCs, not a change here. */
  title?: string | null
}

// The player card: identity + level + the six stats, on a background the player
// earns and equips. It's the header of your own profile and the thing that
// pops up when anyone taps your avatar, so the two can never look different.
export function PlayerCard({
  p,
  actions,
  compact = false,
  statsOnly = false,
  embedded = false,
}: {
  p: PlayerCardData
  /** Buttons pinned beside the name — Edit / settings on your own profile. */
  actions?: ReactNode
  /** Tighter padding, for the pop-up where vertical space is scarcer. */
  compact?: boolean
  /**
   * Drop the identity block — avatar, name, title, denomination, XP bar — and
   * keep only the six stats.
   *
   * Used wherever ProfileHero is already showing the player at full size
   * directly above — your own profile, and now the pop-up. Repeating the avatar
   * and handle underneath a portrait is the same person twice on one screen,
   * and the numbers are the part the card is actually carrying there. The
   * denomination and title move up into the hero's caption rather than being
   * dropped.
   *
   * The card still keeps its identity anywhere it stands genuinely alone.
   */
  statsOnly?: boolean
  /**
   * Draw no card of its own — no background, border or tiles — so the level and
   * the five stats can sit on ProfileHero's painting as one surface (your own
   * /you). The numbers are the same numbers; only the frame goes.
   */
  embedded?: boolean
}) {
  // The Cornerstone writes in gold; every other card in ink. Decided by the
  // background's definition, never here.
  const gold = cardBgInk(p.cardBackground) === 'gold'
  const denom = p.denomination ? denominationName(p.denomination) : null
  // SVG gradient ids must be unique per rendered card — the profile header and
  // an open pop-up can be on screen at once.
  const artId = `pc-${p.username}-${p.cardBackground ?? 'default'}`

  if (embedded) {
    return (
      <div>
        <FounderPill username={p.username} />
        <LevelRow xp={p.xp} gold={gold} compact />
        {hasAbout({ verse: p.favoriteVerse, book: p.favoriteBook, translation: p.favoriteTranslation }) && (
          <div style={{ marginTop: 12 }}>
            <AboutStrip verse={p.favoriteVerse} book={p.favoriteBook} translation={p.favoriteTranslation} gold={gold} />
          </div>
        )}
        {/* Five across, divided by hairlines rather than boxed — on the
            painting the tiles read as a second card laid over the first. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', marginTop: 14 }}>
          <RowStat first label="Streak" value={`${p.currentStreak}`} art="stat_streak" gold={gold} />
          <RowStat label="Longest" value={`${p.longestStreak}d`} art="stat_longest" gold={gold} />
          <RowStat label="Cards" value={`${p.cards}`} art="stat_cards" gold={gold} />
          <RowStat label="Total XP" value={p.xp.toLocaleString()} art="stat_xp" gold={gold} />
          <RowStat label="Plays" value={`${p.totalPlays}`} art="stat_plays" gold={gold} />
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        ...cardBgStyle(p.cardBackground),
        borderRadius: 'var(--r-lg, 22px)',
        border: '1px solid var(--stroke)',
        padding: compact ? '16px 14px' : '18px 16px',
        overflow: 'hidden',
        position: 'relative',
        boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
      }}
    >
      <CardBg bgKey={p.cardBackground} id={artId} eager />
      {/* A scrim keeps text legible over the brighter paintings without
          flattening them. */}
      <div
        aria-hidden
        style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(9, 6, 8,0.20) 0%, rgba(9, 6, 8,0.44) 100%)',
        }}
      />
      <div style={{ position: 'relative' }}>
      {/* The founder's card sells the founding patron from the card itself —
          a slim gold bar, so anyone who taps the founder on a board sees the
          ask without scrolling. Nothing for anybody else's card, and nothing
          when a sale can't be shown; FounderPill decides both. */}
      <FounderPill username={p.username} />

      {/* Actions get their own row above the handle rather than sitting beside
          it, so neither a long username nor a wide button ("✨ Customize") ever
          crowds the other. */}
      {actions && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginBottom: 10 }}>
          {actions}
        </div>
      )}

      {/* Identity */}
      {!statsOnly && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
        <Avatar
          emoji={p.avatarEmoji}
          character={p.avatarCharacter}
          size={compact ? 56 : 64}
          ring
          border={p.avatarBorder}
          badge={p.avatarBadge}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2
            className={gold ? 'gold-text' : undefined}
            style={{
              fontSize: compact ? 21 : 24, overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: 'nowrap', minWidth: 0,
              textShadow: '0 2px 10px rgba(0,0,0,0.55)',
            }}
          >
            @{p.username}
          </h2>
          <FounderTag username={p.username} size="sm" />
          {p.title && (
            <div
              className="faint"
              style={{
                fontSize: 12,
                fontWeight: 700,
                marginTop: 1,
                color: 'var(--gold)',
                textShadow: '0 2px 8px rgba(0,0,0,0.6)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {p.title}
            </div>
          )}
          {denom && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: denominationColor(p.denomination!), boxShadow: `0 0 7px ${denominationColor(p.denomination!)}` }} />
              <span className="faint" style={{ fontSize: 11.5, fontWeight: 700 }}>{denom}</span>
            </div>
          )}
          <div style={{ marginTop: 8 }}><XpBar xp={p.xp} /></div>
        </div>
      </div>
      )}

      {/* Without the identity block the level bar has nowhere to live, and it's
          the one number that reads as progress rather than a total — so it
          moves above the tiles rather than being dropped. */}
      {statsOnly && (
        <div style={{ ...TILE, padding: '14px 14px', marginBottom: 12 }}>
          <LevelRow xp={p.xp} gold={gold} />
        </div>
      )}

      {/* What they say about themselves — between the level and the numbers,
          because it is the one thing on the card that is neither. Renders
          nothing at all when nothing is set, so an untouched card is exactly
          the card it always was. */}
      {hasAbout({ verse: p.favoriteVerse, book: p.favoriteBook, translation: p.favoriteTranslation }) && (
        <AboutStrip
          verse={p.favoriteVerse}
          book={p.favoriteBook}
          translation={p.favoriteTranslation}
          gold={gold}
        />
      )}

      {/* Five stats. Level was the sixth and it is already the headline of the
          bar directly above ("LVL 7 · 83/100 XP"), so the tile said the same
          number twice on one card. Three across, then two, each spanning
          half — a six-column grid so the second row is two even halves rather
          than an orphan. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10 }}>
        <div style={{ gridColumn: 'span 2' }}><Stat label="Streak" value={`${p.currentStreak}`} art="stat_streak" gold={gold} /></div>
        <div style={{ gridColumn: 'span 2' }}><Stat label="Longest" value={`${p.longestStreak}d`} art="stat_longest" gold={gold} /></div>
        <div style={{ gridColumn: 'span 2' }}><Stat label="Cards" value={`${p.cards}`} art="stat_cards" gold={gold} /></div>
        <div style={{ gridColumn: 'span 3' }}><Stat label="Total XP" value={p.xp.toLocaleString()} art="stat_xp" gold={gold} /></div>
        <div style={{ gridColumn: 'span 3' }}><Stat label="Plays" value={`${p.totalPlays}`} art="stat_plays" gold={gold} /></div>
      </div>
      </div>
    </div>
  )
}

// The tile every block on the card sits in: its own scrim over the painting,
// because a bright background would otherwise wash the text out.
const TILE: React.CSSProperties = {
  borderRadius: 'var(--r-md, 16px)',
  background: 'rgba(10,4,28,0.5)',
  border: '1px solid rgba(255,255,255,0.10)',
  backdropFilter: 'blur(3px)',
}

const EYEBROW: React.CSSProperties = {
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}

// The favorite-verse / favorite-book / translation strip. ONE tile rather than
// three, so it reads as a sentence about a person instead of three more stats:
// the verse across the top with its reference signed underneath, and the two
// short facts side by side on one line below a hairline. Everything in it is a
// pick from a fixed catalog (data/cardAbout) — the text is the pool's, never
// anything the player typed — and none of it is a number.
//
// A verse the arcade doesn't carry shows its reference alone; a card with only
// a book, or only a translation, gets the bottom row and nothing above it. The
// tile never grows past three lines of verse (line-clamp), so the card stays
// a card and not a page.
function AboutStrip({
  verse,
  book,
  translation,
  gold,
}: {
  verse?: string | null
  book?: string | null
  translation?: string | null
  gold: boolean
}) {
  const seed = verse ? aboutVerseSeed(verse) : undefined
  const tr = translationByCode(translation)
  const facts: { label: string; value: string; sub?: string }[] = []
  if (book) facts.push({ label: 'Favorite book', value: book })
  if (tr) facts.push({ label: 'Translation', value: tr.code, sub: tr.name })
  const valueClass = gold ? 'gold-text' : undefined

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      style={{ ...TILE, padding: '9px 12px 10px', marginBottom: 10, display: 'grid', gap: 8 }}
    >
      {verse && (
        <div style={{ minWidth: 0 }}>
          {/* The reference shares the eyebrow's line rather than signing off
              underneath: one line fewer, and the quote still reads as quoted. */}
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
            <span className="faint" style={{ ...EYEBROW, flexShrink: 0 }}>Favorite verse</span>
            <span
              className={valueClass}
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 13,
                fontWeight: 800,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textShadow: '0 1px 4px rgba(0,0,0,0.5)',
              }}
            >
              {verse}
            </span>
          </div>
          {seed && (
            <p
              style={{
                margin: '4px 0 0',
                fontSize: 12.5,
                lineHeight: 1.4,
                fontStyle: 'italic',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textShadow: '0 1px 4px rgba(0,0,0,0.5)',
              }}
            >
              “{seed.text}”
            </p>
          )}
        </div>
      )}
      {facts.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: facts.length === 2 ? '1fr 1fr' : '1fr',
            gap: 12,
            borderTop: verse ? '1px solid rgba(255,255,255,0.10)' : 'none',
            paddingTop: verse ? 8 : 0,
          }}
        >
          {facts.map((f) => (
            <div key={f.label} style={{ minWidth: 0 }}>
              <div className="faint" style={EYEBROW}>{f.label}</div>
              <div
                style={{
                  marginTop: 2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textShadow: '0 1px 4px rgba(0,0,0,0.5)',
                }}
                title={f.sub}
              >
                <span className={valueClass} style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 800 }}>
                  {f.value}
                </span>
                {f.sub && <span className="faint" style={{ fontSize: 10.5, marginLeft: 6 }}>{f.sub}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  )
}

// Tiles sit on painted artwork, so they carry their own scrim instead of the
// usual .card surface — otherwise a bright background washes the numbers out.
//
// Every tile is the same three rows at the same heights — icon, number, label —
// so the five line up across a row whatever they hold. The streak's icon is the
// equipped flame (it still flickers and grows hotter with the streak); the rest
// are the drawn set from data/icons in gold.
function Stat({ label, value, art, gold }: { label: string; value: string; art: PaintedIconId; gold?: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      style={{ ...TILE, padding: '12px 8px 11px', textAlign: 'center', display: 'grid', justifyItems: 'center', gap: 4 }}
    >
      <div aria-hidden style={{ height: 28, display: 'grid', placeItems: 'center', lineHeight: 0 }}>
        <PaintedIcon id={art} size={26} />
      </div>
      <div
        className={gold ? 'gold-text' : undefined}
        style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 800, lineHeight: 1.1 }}
      >
        {value}
      </div>
      <div className="faint" style={{ ...EYEBROW, textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>{label}</div>
    </motion.div>
  )
}

// One column of the embedded stat row: icon, number, label at fixed heights,
// with a hairline on its left so five read as one row and not five boxes.
function RowStat({ label, value, art, gold, first = false }: { label: string; value: string; art: PaintedIconId; gold?: boolean; first?: boolean }) {
  return (
    <div
      style={{
        display: 'grid',
        justifyItems: 'center',
        gap: 3,
        minWidth: 0,
        padding: '2px 2px',
        borderLeft: first ? 'none' : '1px solid rgba(255,255,255,0.12)',
        textShadow: '0 1px 4px rgba(9, 6, 8,0.9)',
      }}
    >
      <div aria-hidden style={{ height: 28, display: 'grid', placeItems: 'center', lineHeight: 0 }}>
        <PaintedIcon id={art} size={26} />
      </div>
      <div
        className={gold ? 'gold-text' : undefined}
        style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(15px, 4.6vw, 20px)', fontWeight: 800, lineHeight: 1.1, whiteSpace: 'nowrap' }}
      >
        {value}
      </div>
      <div className="faint" style={{ fontSize: 'clamp(10px, 2.9vw, 12px)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{label}</div>
    </div>
  )
}
